import { useEffect, useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useStudioStore } from '@/store'
import { supabase } from '@/lib/supabase'
import { StudioRankingData, StudioSubscriptionType, DailyTotals } from '@/types'
import { Card, ProgressBar } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { SalesFunnel } from '@/components/SalesFunnel'
import { loadStudioRankingsFromView } from '@/lib/rankings'
import { formatCurrency, formatNumber, formatNumberRounded, formatPercentage, formatPeriod } from '@/lib/utils'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { buildFunnelPlanFromWeeklyRows, formatDateLocalIso } from '@/lib/weeklyRangePlan'
import type { StudioCityRef } from '@/lib/partnerFeatures'
// Импортируем созданные хуки
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { useStudioAnalytics } from '@/hooks/useStudioAnalytics'
import { useAnalyticsMetrics, type WeekMetrics } from '@/hooks/useAnalyticsMetrics'
import { useMetricWeeks } from '@/hooks/useMetricWeeks'
import { useWeeklyPlan } from '@/hooks/useWeeklyPlan'
import { useWeeklyPlanVsFact } from '@/hooks/useWeeklyPlanVsFact'
import type { WeeklyPlanVsFactRow } from '@/hooks/useWeeklyPlanVsFact'
import { useChartData } from '@/hooks/useChartData'
import { useFactorAnalysis } from '@/hooks/useFactorAnalysis'
import { useSalesMixAnalytics } from '@/hooks/useSalesMixAnalytics'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useAnalyticsChartsState } from '@/hooks/useAnalyticsChartsState'
import { useCrmRevenueComparison } from '@/hooks/useCrmRevenueComparison'
import { useStudioPlanDiagnostics } from '@/hooks/useStudioPlanDiagnostics'
import {
  calculateClientRetention,
  resolveRangeEndMetricValue,
  resolveRangeGrowthStartMetricValue,
  resolveRangeStartMetricValue
} from '@/lib/analytics/clientMetrics'
// Импортируем новые компоненты аналитики
import { 
  PlanVsFactSection, 
  PeriodSelector, 
  DailyPeriodMetricsTable,
  AnalyticsDashboardLayout,
  AnalyticsKpiGrid,
  AnalyticsChartsSection,
  CrmComparisonModal
} from '@/components/analytics'

export function AnalyticsPage() {
  const { studioId } = useParams<{ studioId: string }>()
  const navigate = useNavigate()
  const { studios } = useStudioStore()
  const studioIntId = parseInt(studioId || '0')
  
  // ========================================
  // State: UI и фильтры
  // ========================================
  const [rankingData, setRankingData] = useState<StudioRankingData | null>(null)
  const [rankingLoading, setRankingLoading] = useState(true)
  const [studioName, setStudioName] = useState(`Студия №${studioId}`)
  const [subscriptionType, setSubscriptionType] = useState<StudioSubscriptionType>('REGULAR')
  const [filteredFunnelData, setFilteredFunnelData] = useState({ leads: 0, appointments: 0, visits: 0, sales: 0 })
  const [funnelPlanData, setFunnelPlanData] = useState<any>(null)
  const [funnelDailyBreakdown, setFunnelDailyBreakdown] = useState<any[]>([])
  const [funnelTrendData, setFunnelTrendData] = useState<any[]>([])
  const [funnelPeriodStart, setFunnelPeriodStart] = useState<string | null>(null)
  const [funnelPeriodEnd, setFunnelPeriodEnd] = useState<string | null>(null)
  const {
    chartMode,
    setChartMode,
    conversionWindow,
    setConversionWindow,
    chartView,
    setChartView,
    visibleLines,
    setVisibleLines
  } = useAnalyticsChartsState()
  const [showCrmDetails, setShowCrmDetails] = useState(false)
  
  // ========================================
  // Хуки для данных (заменяют множество useState и логики)
  // ========================================
  const periods = useStudioPeriods(studioIntId)
  const analytics = useStudioAnalytics({
    studioId: studioIntId,
    currentPeriod: periods.currentPeriod
  })
  
  // Загружаем данные о текущей и предыдущей неделе
  const { currentWeek, prevWeek, loading: weeksLoading } = useMetricWeeks(
    periods.currentPeriod,
    periods.isCurrentPeriod
  )
  
  // Загружаем недельный план для текущей недели
  const { weeklyPlan, loading: weeklyPlanLoading } = useWeeklyPlan(
    studioIntId,
    currentWeek
  )
  const { weeks: weeklyPlanVsFactWeeks } = useWeeklyPlanVsFact({
    studioId: studioIntId || null,
    periodId: periods.currentPeriod?.period_id || null
  })
  
  const metrics = useAnalyticsMetrics({
    dailyData: analytics.dailyData,
    planData: analytics.planData,
    previousPeriodData: analytics.previousPeriodData,
    currentPeriod: periods.currentPeriod,
    totalMetrics: analytics.analytics?.metrics || null,
    currentWeek,
    prevWeek
  })
  const { chartData } = useChartData({
    dailyData: analytics.dailyData,
    conversionDailyData: analytics.conversionDailyData,
    planData: analytics.planData,
    conversionWindow
  })
  const factorAnalysis = useFactorAnalysis({
    studioId: studioIntId,
    dailyData: analytics.dailyData,
    currentPeriod: periods.currentPeriod,
    planData: analytics.planData
  })

  // ========================================
  // Сокращенные ссылки для удобства (объявляем ДО использования в useEffect)
  // ========================================
  const currentPeriod = periods.currentPeriod
  const currentAnalytics = analytics.analytics
  const monthlyData = analytics.dailyData
  const conversionDailyData = analytics.conversionDailyData
  const planData = analytics.planData
  const previousPeriodData = analytics.previousPeriodData
  const currentWeekMetrics = metrics.currentWeekMetrics
  const isCurrentPeriod = periods.isCurrentPeriod
  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    availableWeekOptions,
    timeFilterOptions,
    selectedRange
  } = useAnalyticsTimeFilter(currentPeriod)
  const currentStudioNumber = useMemo(
    () => studios.find((s) => s.studio_internal_id === studioIntId)?.studio_number || String(studioIntId),
    [studios, studioIntId]
  )
  const currentStudio = useMemo(
    () => studios.find((s) => s.studio_internal_id === studioIntId) || null,
    [studios, studioIntId]
  )
  const isManagementCompanyAnalyticsViewer = currentStudio?.access_kind === 'management_company' || currentStudio?.role === 'management_company'
  const canViewAnalyticsDecisions = Boolean(currentStudio)
  // Зеркало серверного гейта set_studio_weekly_metric_decision: писать могут
  // владелец и управляющая, остальные (member, УК) — только смотреть.
  const canEditAnalyticsDecisions = Boolean(
    currentStudio && !isManagementCompanyAnalyticsViewer && (currentStudio.role === 'owner' || currentStudio.role === 'manager')
  )
  // Город студии решает, какая версия таблицы метрик показывается (пилот — только Вологда).
  const analyticsTableCities = useMemo<StudioCityRef[]>(
    () => (currentStudio ? [{ cityId: currentStudio.city_id, cityName: currentStudio.city?.city_name }] : []),
    [currentStudio]
  )
  const studioCurrency = useMemo(
    () => studios.find((s) => s.studio_internal_id === studioIntId)?.currency_code || 'RUB',
    [studios, studioIntId]
  )
  const crmStudioNumber = useMemo(() => {
    const raw = studios.find((s) => s.studio_internal_id === studioIntId)?.studio_number
    if (raw === undefined || raw === null) return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : null
  }, [studios, studioIntId])
  const {
    missingPlanStudios,
    zeroRevenueStudios,
    zeroPlanRevenueStudios,
    shouldHideDelta
  } = useStudioPlanDiagnostics({
    weeklyPlanVsFactWeeks,
    planData,
    studioNumber: currentStudioNumber,
    totalRevenue: currentAnalytics?.metrics.total_revenue || 0
  })
  const selectedDailyData = useMemo(() => {
    if (!selectedRange) return monthlyData
    return monthlyData.filter((day) => day.metric_date >= selectedRange.start && day.metric_date <= selectedRange.end)
  }, [monthlyData, selectedRange])
  const selectedWeekPlanRow = useMemo(() => {
    if (!selectedRange || selectedRange.mode !== 'week' || selectedRange.weekNumber === null) return null
    return weeklyPlanVsFactWeeks.find((week) => week.week_number_in_period === selectedRange.weekNumber) || null
  }, [selectedRange, weeklyPlanVsFactWeeks])
  const selectedCardViewMode: 'period' | 'current_week' = selectedRange?.mode === 'week' ? 'current_week' : 'period'
  const selectedWeekForCards = useMemo(() => {
    if (!selectedRange || selectedRange.mode !== 'week') return currentWeek
    const weekNumber = selectedRange.weekNumber || 1
    return {
      week_id: selectedWeekPlanRow?.week_id || -weekNumber,
      period_id: currentPeriod?.period_id || 0,
      week_number_in_period: weekNumber,
      week_start: selectedRange.start,
      week_end: selectedRange.end,
      week_len_days: selectedRange.rangeDays
    }
  }, [selectedRange, selectedWeekPlanRow, currentPeriod?.period_id, currentWeek])
  const selectedWeekMetrics = useMemo(() => {
    if (!selectedRange || selectedRange.mode !== 'week') return null
    return {
      total_revenue: selectedDailyData.reduce((sum, day) => sum + (day.gross_revenue || 0), 0),
      days_with_data: selectedDailyData.length,
      week_len_days: selectedRange.rangeDays
    } as WeekMetrics
  }, [selectedRange, selectedDailyData])
  const selectedWeeklyPlanData = useMemo(() => {
    if (!selectedWeekPlanRow) return weeklyPlan
    return {
      weekly_gross_revenue_plan: selectedWeekPlanRow.weekly_gross_revenue_plan || 0,
      weekly_new_sales_amount_plan: selectedWeekPlanRow.weekly_new_sales_amount_plan || 0,
      weekly_renewals_amount_plan: selectedWeekPlanRow.weekly_renewals_amount_plan || 0,
      weekly_returning_clients_amount_plan: selectedWeekPlanRow.weekly_returning_clients_amount_plan || 0,
      weekly_upgrades_amount_plan: selectedWeekPlanRow.weekly_upgrades_amount_plan || 0,
      weekly_leads_plan: selectedWeekPlanRow.weekly_leads_plan || 0,
      weekly_appointments_plan: selectedWeekPlanRow.weekly_appointments_plan || 0,
      weekly_new_visitors_plan: selectedWeekPlanRow.weekly_new_visitors_plan || 0,
      weekly_new_sales_plan: (selectedWeekPlanRow.weekly_new_sales_plan || 0) + (selectedWeekPlanRow.weekly_new_subscriptions_count_plan || 0)
    }
  }, [selectedWeekPlanRow, weeklyPlan])
  const todayIso = getLocalIsoDate()
  const selectedClientsAtStart = useMemo(() => {
    return resolveRangeGrowthStartMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: conversionDailyData,
      key: 'total_clients_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end,
      fallbackCurrentValue: currentAnalytics?.metrics.total_clients_count,
      fallbackGrowth: currentAnalytics?.metrics.clients_growth,
      allowGrowthFallback: !selectedRange || selectedRange.mode !== 'week'
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, selectedDailyData, conversionDailyData, currentAnalytics?.metrics.total_clients_count, currentAnalytics?.metrics.clients_growth])
  const selectedClientsAtEnd = useMemo(() => {
    return resolveRangeEndMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: conversionDailyData,
      key: 'total_clients_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end,
      todayIso
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, todayIso, selectedDailyData, conversionDailyData])
  const selectedSubscribersAtStart = useMemo(() => {
    return resolveRangeGrowthStartMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: conversionDailyData,
      key: 'active_subscribers_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, selectedDailyData, conversionDailyData])
  const selectedSubscribersAtEnd = useMemo(() => {
    return resolveRangeEndMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: conversionDailyData,
      key: 'active_subscribers_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end,
      todayIso
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, todayIso, selectedDailyData, conversionDailyData])
  const selectedVisitorsAtStart = useMemo(() => {
    return resolveRangeGrowthStartMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: conversionDailyData,
      key: 'active_visitors_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, selectedDailyData, conversionDailyData])
  const selectedVisitorsAtEnd = useMemo(() => {
    return resolveRangeEndMetricValue({
      dataInRange: selectedDailyData,
      dataBeforeRange: conversionDailyData,
      key: 'active_visitors_count',
      rangeStart: selectedRange?.start || currentPeriod?.period_start,
      rangeEnd: selectedRange?.end || currentPeriod?.period_end,
      todayIso
    })
  }, [selectedRange, currentPeriod?.period_start, currentPeriod?.period_end, todayIso, selectedDailyData, conversionDailyData])
  const selectedClientsCount = selectedClientsAtEnd
  const selectedSubscribersCount = selectedSubscribersAtEnd
  const selectedVisitorsCount = selectedVisitorsAtEnd
  const selectedClientsGrowth = useMemo(() => {
    if (selectedClientsAtStart === undefined || selectedClientsCount === undefined) return undefined
    return selectedClientsCount - selectedClientsAtStart
  }, [selectedClientsAtStart, selectedClientsCount])
  const selectedSubscribersGrowth = useMemo(() => {
    if (selectedSubscribersAtStart === undefined || selectedSubscribersCount === undefined) return undefined
    return selectedSubscribersCount - selectedSubscribersAtStart
  }, [selectedSubscribersAtStart, selectedSubscribersCount])
  const selectedVisitorsGrowth = useMemo(() => {
    if (selectedVisitorsAtStart === undefined || selectedVisitorsCount === undefined) return undefined
    return selectedVisitorsCount - selectedVisitorsAtStart
  }, [selectedVisitorsAtStart, selectedVisitorsCount])
  const selectedClientsRetention = useMemo(() => {
    if (selectedClientsAtEnd === undefined) return undefined
    const newClientsCount = selectedDailyData.reduce((sum, day) => sum + (day.new_sales_count || 0), 0)
    const returningClientsCount = selectedDailyData.reduce((sum, day) => sum + (day.returning_clients_count || 0), 0)

    return calculateClientRetention({
      clientsAtStart: selectedClientsAtStart,
      clientsAtEnd: selectedClientsAtEnd,
      newClientsCount,
      returningClientsCount
    })
  }, [selectedDailyData, selectedClientsAtStart, selectedClientsAtEnd, selectedClientsCount])
  const selectedSubscribersRetention = useMemo(() => {
    if (selectedSubscribersAtEnd === undefined) return undefined
    const newSubscribersCount = selectedDailyData.reduce((sum, day) => sum + (day.new_subscriptions_count || 0), 0)
    const upgradedSubscribersCount = selectedDailyData.reduce(
      (sum, day) => sum + (day.subscription_upgrades_count || 0),
      0
    )
    const returningSubscribersCount = selectedDailyData.reduce(
      (sum, day) => sum + (day.subscription_returning_clients_count || 0),
      0
    )

    return calculateClientRetention({
      clientsAtStart: selectedSubscribersAtStart,
      clientsAtEnd: selectedSubscribersAtEnd,
      newClientsCount: newSubscribersCount,
      upgradesCount: upgradedSubscribersCount,
      returningClientsCount: returningSubscribersCount
    })
  }, [selectedDailyData, selectedSubscribersAtStart, selectedSubscribersAtEnd, selectedSubscribersCount])

  const {
    crmLoading,
    crmError,
    crmLastSyncAt,
    crmTotals,
    crmPaymentChannels
  } = useCrmRevenueComparison({
    crmStudioNumber,
    selectedRange,
    dailyData: monthlyData
  })
  const {
    recurringClientsAnalytics,
    newSalesAnalytics,
    autoRenewalsAnalytics,
    recurringSalesBreakdown,
    newSalesBreakdown,
    recurringSalesDetails,
    refundsAmount,
    recurringShareWithoutRefundsPct,
    newSalesShareWithoutRefundsPct,
    autoRenewalsShareWithoutRefundsPct,
    refundsSharePct
  } = useSalesMixAnalytics({
    selectedRange: selectedRange
      ? { mode: selectedRange.mode, start: selectedRange.start, end: selectedRange.end }
      : null,
    selectedDailyData,
    selectedWeekPlanRow,
    planData
  })

  // ========================================
  // Константы для легенд графиков
  // ========================================
  const volumeLegendItems = [
    { key: 'leads', label: 'Заявки', color: '#FF9800' },
    { key: 'appointments', label: 'Записи', color: '#9C27B0' },
    { key: 'visitors', label: 'Приходы', color: '#2196F3' },
    { key: 'sales', label: 'Продажи', color: '#E91E63' }
  ]

  const conversionLegendItems = [
    { key: 'cr1', label: 'Заявка → Запись', color: '#FF9800' },
    { key: 'cr2', label: 'Запись → Приход', color: '#9C27B0' },
    { key: 'cr3', label: 'Приход → Продажа', color: '#2196F3' },
    { key: 'crTotal', label: 'Общая конверсия', color: '#4CAF50' }
  ]

  const revenueLegendItems = [
    { key: 'revenue', label: 'Общая выручка', color: '#4CAF50' },
    { key: 'newRevenue', label: 'От новых', color: '#2196F3' },
    { key: 'existingRevenue', label: 'От текущих', color: '#FF9800' }
  ]

  // ========================================
  // Эффекты: Загрузка дополнительных данных
  // ========================================
  useEffect(() => {
    if (studioIntId) {
      loadStudioSubscriptionType()
      loadRankings()
    }
  }, [studioIntId])

  const loadStudioSubscriptionType = async () => {
    try {
      const { data, error } = await supabase
        .from('studio_subscription_settings')
        .select('subscription_type')
        .eq('studio_internal_id', studioIntId)
        .maybeSingle()
      
      if (!error && data) {
        const type = (data as any).subscription_type || 'REGULAR'
        setSubscriptionType(type)
      } else {
        setSubscriptionType('REGULAR')
      }
    } catch (err) {
      console.error('Error loading studio subscription type:', err)
      setSubscriptionType('REGULAR')
    }
  }

  const loadRankings = async () => {
    try {
      setRankingLoading(true)
      const rankings = await loadStudioRankingsFromView(studioIntId)
      setRankingData(rankings)

      // Также получаем информацию о студии для правильного названия
      const month = getLocalIsoDate().slice(0, 7)
      const { data: studioInfo, error } = await (supabase as any)
        .rpc('get_studio_rankings', {
          target_studio_id: studioIntId,
          target_month: month
        })

      if (!error && studioInfo && studioInfo.length > 0) {
        const studio = studioInfo[0]
        if (studio.city_name && studio.studio_number) {
          setStudioName(`${studio.city_name} (№${studio.studio_number})`)
        }
      }
    } catch (error) {
      console.error('Error loading rankings:', error)
    } finally {
      setRankingLoading(false)
    }
  }

  // ========================================
  // УСТАРЕВШИЕ ФУНКЦИИ УДАЛЕНЫ
  // Вся логика загрузки данных, расчетов и управления периодами
  // теперь находится в хуках:
  // - useStudioPeriods (периоды и навигация)
  // - useStudioAnalytics (загрузка данных)
  // - useAnalyticsMetrics (расчет метрик и конверсий)
  // - useChartData (данные для графиков)
  // ========================================

  // Функция для загрузки данных для предыдущего периода для сравнения в карточках - теперь находится в хуке useStudioAnalytics

  // Функция для расчета метрик за текущую неделю (понедельник-сегодня) - удалена, так как вся логика теперь в хуке useAnalyticsMetrics

  // Пересчитываем метрики за текущую неделю при изменении данных - удалено, так как вся логика теперь в хуке useAnalyticsMetrics

  // Функция для загрузки плана для конкретного диапазона дат
  const loadPlanForDateRange = useCallback(async (startDate: Date, endDate: Date) => {
    try {
      const start = formatDateLocalIso(startDate)
      const end = formatDateLocalIso(endDate)

      const { data: weeklyPlans, error } = await supabase
        .from('studio_weekly_plan_vs_fact')
        .select('*')
        .eq('studio_internal_id', studioIntId)
        .lte('week_start', end)
        .gte('week_end', start)
        .order('week_start', { ascending: true }) as { data: any[] | null, error: any }

      if (error) return null
      return buildFunnelPlanFromWeeklyRows(weeklyPlans, startDate, endDate)
    } catch (err) {
      console.error('Error loading plan for date range:', err)
      return null
    }
  }, [studioIntId])

  // Функция для расчета данных воронки на основе выбранного фильтра
  const calculateFilteredFunnelData = useCallback(async () => {
    if (!currentPeriod || !planData || !selectedRange) {
      setFilteredFunnelData({ leads: 0, appointments: 0, visits: 0, sales: 0 })
      setFunnelPlanData(null)
      setFunnelDailyBreakdown([])
      setFunnelPeriodStart(null)
      setFunnelPeriodEnd(null)
      return
    }

    const rangeStartDate = new Date(selectedRange.start)
    const rangeEndDate = new Date(selectedRange.end)
    const filteredData = monthlyData.filter((day) => day.metric_date >= selectedRange.start && day.metric_date <= selectedRange.end)

    // Сохраняем теоретические границы периода для отображения
    setFunnelPeriodStart(selectedRange.start)
    setFunnelPeriodEnd(selectedRange.end)

    // Рассчитываем суммы для отфильтрованных данных
    const totalLeads = filteredData.reduce((sum: number, day: any) => sum + (day.leads_count || 0), 0)
    const totalAppointments = filteredData.reduce((sum: number, day: any) => sum + (day.appointments_count || 0), 0)
    const totalVisits = filteredData.reduce((sum: number, day: any) => sum + (day.new_visitors_count || 0), 0)
    const totalSales = filteredData.reduce((sum: number, day: any) => (
      sum + (day.new_sales_count || 0) + (day.new_subscriptions_count || 0)
    ), 0)
    
    setFilteredFunnelData({
      leads: totalLeads,
      appointments: totalAppointments,
      visits: totalVisits,
      sales: totalSales
    })
    
    // Обновляем дневную разбивку для микро-гистограммы
    setFunnelDailyBreakdown(filteredData.map((day: any) => ({
      date: day.metric_date,
      leads: day.leads_count || 0,
      appointments: day.appointments_count || 0,
      visits: day.new_visitors_count || 0,
      sales: (day.new_sales_count || 0) + (day.new_subscriptions_count || 0)
    })))
    
    // Загружаем план для диапазона дат
    const planForRange = await loadPlanForDateRange(rangeStartDate, rangeEndDate)
    if (planForRange) {
      setFunnelPlanData(planForRange)
    } else {
      // TODO(weekly-plan-vs-fact): удалить fallback после стабилизации weekly view на всех окружениях.
      // Временный вариант: линейное распределение по дням периода.
      const totalPeriodDays = Math.max(1, currentPeriod.period_len_days || 1)
      const rangeRatio = selectedRange.rangeDays / totalPeriodDays
      const leads = Math.round((planData.leads_count_plan || 0) * rangeRatio)
      const appointments = Math.round((planData.appointments_count_plan || 0) * rangeRatio)
      const visits = Math.round((planData.new_visitors_count_plan || 0) * rangeRatio)
      const sales = Math.round(((planData.new_sales_count_plan || 0) + (planData.new_subscriptions_count_plan || 0)) * rangeRatio)
      
      setFunnelPlanData({
        leads,
        appointments,
        visits,
        sales,
        bookingConversionPlan: leads > 0 ? (appointments / leads) * 100 : 0,
        visitConversionPlan: appointments > 0 ? (visits / appointments) * 100 : 0,
        salesConversionPlan: visits > 0 ? (sales / visits) * 100 : 0
      })
    }
  }, [monthlyData, currentPeriod, planData, selectedRange, loadPlanForDateRange])

  // useEffect для пересчета данных воронки при изменении фильтра/данных.
  // Важно: вызываем всегда, чтобы при переходе на студию без данных состояния сбрасывались в нули.
  useEffect(() => {
    calculateFilteredFunnelData()
  }, [calculateFilteredFunnelData])

  // Отображение состояния загрузки
  if (analytics.loading || periods.loading) {
    return <LoadingScreen />
  }

  if (analytics.error || periods.error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center p-8">
          <p className="text-error">{analytics.error || periods.error}</p>
        </Card>
      </div>
    )
  }

  // Проверка: есть ли период, но нет данных
  if (!analytics.analytics || !currentAnalytics) {
    return (
      <div className="space-y-4">
        <PeriodSelector
          currentPeriod={currentPeriod}
          onPrevPeriod={periods.goToPrevPeriod}
          onNextPeriod={periods.goToNextPeriod}
          canGoPrev={periods.canGoPrev}
          canGoNext={periods.canGoNext}
          showViewModeSelector={true}
          isCurrentPeriod={isCurrentPeriod}
          periodFilterValue={selectedTimeFilter}
          periodFilterOptions={timeFilterOptions}
          onPeriodFilterChange={setSelectedTimeFilter}
          selectedRange={selectedRange}
        />
        <div className="min-h-[60vh] bg-background flex items-center justify-center p-4">
          <Card className="text-center p-8">
            <p className="text-muted-foreground">Нет данных за выбранный период</p>
          </Card>
        </div>
      </div>
    )
  }

  if (!periods.currentPeriod) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center p-8">
          <p className="text-muted-foreground">Загрузка периодов...</p>
        </Card>
      </div>
    )
  }

  // Алиасы объявлены выше (после хуков, до useEffect)

  // Данные графиков уже подготовлены хуком useChartData

  return (
    <AnalyticsDashboardLayout containerClassName="max-w-none mx-0 space-y-3">
      {/* Period Selector with Card View Mode Selector - используем компонент */}
      <PeriodSelector
        currentPeriod={currentPeriod}
        onPrevPeriod={periods.goToPrevPeriod}
        onNextPeriod={periods.goToNextPeriod}
        canGoPrev={periods.canGoPrev}
        canGoNext={periods.canGoNext}
        showViewModeSelector={true}
        isCurrentPeriod={isCurrentPeriod}
        periodFilterValue={selectedTimeFilter}
        periodFilterOptions={timeFilterOptions}
        onPeriodFilterChange={setSelectedTimeFilter}
        selectedRange={selectedRange}
      />

      <AnalyticsKpiGrid
        mainMetrics={{
          viewMode: selectedCardViewMode,
          periodMetrics: currentAnalytics.metrics,
          weekMetrics: selectedCardViewMode === 'current_week' ? selectedWeekMetrics : currentWeekMetrics,
          planData,
          weeklyPlanData: selectedWeeklyPlanData,
          currentWeek: selectedWeekForCards,
          currentPeriod,
          periodDaysCount: monthlyData.length || 1,
          crmComparison: {
            appRevenue: selectedDailyData.reduce((sum, day) => sum + (day.gross_revenue || 0), 0),
            crmRevenue: crmTotals.crmRevenue
          },
          onOpenCrmDetails: () => setShowCrmDetails(true),
          currency: studioCurrency
        }}
        funnelData={filteredFunnelData}
        planConversions={{
          cr1Plan: funnelPlanData?.bookingConversionPlan || 0,
          cr2Plan: funnelPlanData?.visitConversionPlan || 0,
          cr3Plan: funnelPlanData?.salesConversionPlan || 0
        }}
        clientsTitle="Клиенты студии"
        clientsCount={selectedClientsCount}
        clientsGrowth={selectedClientsGrowth}
        clientsRetention={selectedClientsRetention}
        subscribersCount={selectedSubscribersCount}
        subscribersGrowth={selectedSubscribersGrowth}
        subscribersRetention={selectedSubscribersRetention}
        visitorsCount={selectedVisitorsCount}
        visitorsGrowth={selectedVisitorsGrowth}
        dailyData={selectedDailyData}
        subscriptionType={subscriptionType}
        newSales={newSalesAnalytics}
        recurringSales={recurringClientsAnalytics}
        autoRenewals={autoRenewalsAnalytics}
        newSalesBreakdown={newSalesBreakdown}
        recurringSalesBreakdown={recurringSalesBreakdown}
        newSalesSharePct={newSalesShareWithoutRefundsPct}
        recurringSharePct={recurringShareWithoutRefundsPct}
        autoRenewalsSharePct={autoRenewalsShareWithoutRefundsPct}
        refundsAmount={refundsAmount}
        refundsSharePct={refundsSharePct}
        currency={studioCurrency}
      />

      {/* План - Факт и Экономика клиента - используем компонент */}
      {planData && (
        <PlanVsFactSection
          planData={planData}
          marketingBudgetFact={currentAnalytics.metrics.marketing_expenses || 0}
          previousPeriodData={previousPeriodData}
          currency={studioCurrency}
        />
      )}

      {/* Sales Funnel */}
      <SalesFunnel
        leads={filteredFunnelData.leads}
        appointments={filteredFunnelData.appointments}
        visits={filteredFunnelData.visits}
        sales={filteredFunnelData.sales}
        leadsplan={funnelPlanData?.leads || 0}
        appointmentsPlan={funnelPlanData?.appointments || 0}
        visitsPlan={funnelPlanData?.visits || 0}
        salesPlan={funnelPlanData?.sales || 0}
        bookingConversionPlan={funnelPlanData?.bookingConversionPlan || 0}
        visitConversionPlan={funnelPlanData?.visitConversionPlan || 0}
        salesConversionPlan={funnelPlanData?.salesConversionPlan || 0}
        dailyBreakdown={funnelDailyBreakdown}
        trendData={funnelTrendData}
        filterValue={selectedTimeFilter}
        onFilterChange={setSelectedTimeFilter}
        availableWeeks={availableWeekOptions}
        periodStartDate={funnelPeriodStart || undefined}
        periodEndDate={funnelPeriodEnd || undefined}
        factorAnalysisData={factorAnalysis.data}
        factorAnalysisVariants={factorAnalysis.variants}
        allowFactorSplitViews={factorAnalysis.canShowSplitViews}
        subscriptionType={subscriptionType}
        recurringSalesDetails={recurringSalesDetails}
        todayDate={getLocalIsoDate()}
        currency={studioCurrency}
      />

      <AnalyticsChartsSection
        chartData={chartData}
        chartMode={chartMode}
        chartView={chartView}
        conversionWindow={conversionWindow}
        visibleLines={visibleLines}
        planData={planData}
        currency={studioCurrency}
        volumeLegendItems={volumeLegendItems}
        conversionLegendItems={conversionLegendItems}
        onChartModeChange={setChartMode}
        onChartViewChange={setChartView}
        onConversionWindowChange={setConversionWindow}
        onToggleVisibleLine={(key) => setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }))}
      />

      <DailyPeriodMetricsTable
        dailyData={monthlyData}
        planData={planData}
        weeklyPlans={weeklyPlanVsFactWeeks}
        cities={analyticsTableCities}
        studioId={studioIntId}
        studioIds={studioIntId ? [studioIntId] : []}
        canEditDecisions={canEditAnalyticsDecisions}
        canViewDecisions={canViewAnalyticsDecisions}
        missingPlanStudios={missingPlanStudios}
        zeroRevenueStudios={zeroRevenueStudios}
        zeroPlanRevenueStudios={zeroPlanRevenueStudios}
        hideDelta={shouldHideDelta}
        hideDeltaStudioCount={1}
        currency={studioCurrency}
        selectedRangeMode={selectedRange?.mode || 'period'}
        selectedRangeWeekNumber={selectedRange?.weekNumber || null}
        onSelectedWeekNumberChange={(weekNumber) => setSelectedTimeFilter(`week_${weekNumber}`)}
      />

      <CrmComparisonModal
        isOpen={showCrmDetails && !!selectedRange}
        rangeLabel={selectedRange?.label || 'период'}
        totals={crmTotals}
        paymentChannels={crmPaymentChannels}
        loading={crmLoading}
        error={crmError}
        lastSyncAt={crmLastSyncAt}
        currency={studioCurrency}
        showSubscriptionRevenue={subscriptionType === 'SUBSCRIPTION' || subscriptionType === 'MIXED'}
        onClose={() => setShowCrmDetails(false)}
      />

    </AnalyticsDashboardLayout>
  )
}
